import { useCallback, useEffect, useState } from 'react';
import { Outlet, useMatches, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import {
  projectQuery,
  SelectedProjectProvider,
  type ProjectDetail as ProjectDetailType,
  type ProjectListItem,
} from '@/entities/project';
import {
  clearProjectPermissions,
  resolveProjectPermissions,
  useHasProjectPermission,
} from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { EditProjectModal, useUpdatePublishedProject } from '@/features/edit-project';
import { CreateFolderModal } from '@/features/manage-project-folders';
import { ApprovalChainsDrawer, useApprovalChainsDrawer } from '@/features/manage-approval-chains';
import { ProjectMembersModal, useProjectMembersModal } from '@/features/manage-project-members';
import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { DeepLinkInterstitial } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import { useModalState, isTerminalResolution } from '@/shared/hooks';
import { useApprovalChainsEnabled } from '@/shared/hooks/useApprovalChainsEnabled';
import { ProjectDetailHeader } from '@/widgets/project-detail-header';
import { ProjectDetailTabs } from '@/widgets/project-detail-tabs';
import { SectionTransition } from './components/SectionTransition';
import { useProjectResolution } from './hooks';
import type { ProjectDetailOutletContext, RouteChrome } from './projectDetail.types';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@monksflow/monks-ui';

/**
 * Find a project list item already in the TanStack cache, regardless of which
 * paginated / filtered list query it was fetched under (MFINS-3433).
 *
 * Used as the placeholder for the project-detail query so navigating from the
 * projects list to a single project doesn't flash a full-page loader: the row
 * the user just clicked is already in memory, and `ProjectListItem` shares
 * every field this shell + its outlets read at first paint (title, status,
 * type, createdAt, ...). The real detail query runs in the background and
 * refines `audiences` / `creator` / `snapshot` when it lands.
 */
function findCachedProjectListItem(
  queryClient: ReturnType<typeof useQueryClient>,
  platformProjectId: string | undefined,
  workspaceId: string | null,
): ProjectListItem | undefined {
  if (!platformProjectId || !workspaceId) return undefined;
  // Match only the ACTIVE workspace's list caches: a hit then proves the
  // project lives in the current workspace, which keeps the placeholder
  // honest (never paint a row from a workspace this tab has since left) and
  // lets the deep-link seam skip its resolve gate on the instant-paint path.
  // Key shape is `['projects', 'list', workspaceId, query]` (flat) or
  // `['projects', 'list', 'infinite', workspaceId, query]` (infinite);
  // element containment on the shared prefix covers both.
  //
  // The flat variant resolves to `{ items: [...] }` and the infinite variant
  // to `{ pages: [{ items: [...] }, ...] }` — try both shapes so either
  // surface can supply the placeholder.
  type CachedListData =
    | { items?: ProjectListItem[] }
    | { pages?: Array<{ items?: ProjectListItem[] }> };
  const lists = queryClient.getQueriesData<CachedListData>({
    queryKey: ['projects', 'list'],
  });
  // The URL segment is a platformProjectId for published projects but the
  // internal id for drafts, so match either id space.
  const matchesRef = (item: ProjectListItem): boolean =>
    item.id === platformProjectId || item.platformProjectId === platformProjectId;
  for (const [key, data] of lists) {
    if (!key.includes(workspaceId)) continue;
    if (!data) continue;
    if ('items' in data && data.items) {
      const match = data.items.find(matchesRef);
      if (match) return match;
    }
    if ('pages' in data && data.pages) {
      for (const page of data.pages) {
        const match = page.items?.find(matchesRef);
        if (match) return match;
      }
    }
  }
  return undefined;
}

/**
 * Promote a `ProjectListItem` snapshot to a `ProjectDetail` so it can be
 * handed to TanStack as `placeholderData` (MFINS-3433). The list endpoint
 * never returns the detail-only fields (`state`, `workspaceId`, `audiences`,
 * `creator`, `snapshot`); we leave them undefined / empty until the real
 * query resolves. The shell + outlet code paths that render at first paint
 * (header, sidebar, breadcrumb) only need the fields the list does carry,
 * so the placeholder is sufficient for the user to perceive an instant load.
 */
function listItemToDetailPlaceholder(item: ProjectListItem): ProjectDetailType {
  return {
    ...item,
    // `state` is missing on the list; mark as 'published' (the only state a
    // user can navigate to from the projects table — drafts open the wizard).
    state: 'published',
    // Published detail guarantees these non-null; the list item types them as
    // nullable, so coerce for the transient placeholder until the fetch lands.
    platformProjectId: item.platformProjectId ?? '',
    title: item.title ?? '',
    description: item.description ?? '',
    // The detail-only fields stay undefined / empty; the real fetch fills
    // them in within ~one tick.
    workspaceId: '',
    audiences: undefined,
    creator: undefined,
    snapshot: undefined,
  };
}

/**
 * Project detail shell. Renders the project sidebar, one project header, and the
 * active section via `<Outlet />`.
 *
 * The header lives here rather than in each section: it names the project, not
 * the section, so it is the same on every one of them and survives moving
 * between them. Sections render their own content only.
 *
 * Owns shared modals (Create Folder, Project Members, Edit Project) so they
 * survive section navigation and can be opened from any section via the outlet
 * context.
 */
export function ProjectDetail() {
  const { t } = useTranslation('projects');

  const { platformProjectId } = useParams<{ platformProjectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Seed the caller's per-project permissions (v2 /auth/permissions) when the
  // project page loads, so every section can gate its controls. Consumers
  // (`useProjectCapabilities`) refresh it lazily when it goes stale — no fixed
  // poll. Cleared on unmount so a stale project's capabilities never leak into
  // the next one.
  useEffect(() => {
    if (!platformProjectId) return undefined;
    resolveProjectPermissions(platformProjectId);
    return () => clearProjectPermissions();
  }, [platformProjectId]);

  // MFINS-3433 — when the user clicks a project from the list, the row data
  // is already in the cache. Hand it to the detail query as `placeholderData`
  // so the shell paints instantly with the known fields (title, status,
  // type, …) and the real fetch refines the detail-only fields in the
  // background. Eliminates the full-page loader flash designer feedback
  // flagged. Falls back to the original loader for direct-URL navigation
  // where no list cache exists.
  const activeWorkspaceId = useCurrentWorkspaceId();
  const cachedListItem = findCachedProjectListItem(
    queryClient,
    platformProjectId,
    activeWorkspaceId,
  );
  const placeholder = cachedListItem ? listItemToDetailPlaceholder(cachedListItem) : undefined;

  // Deep-link seam: resolve the id's owning workspace first and
  // hold the project fetch until resolution settles — under the wrong
  // workspace header it would 404 into the error branch prematurely. An
  // active-workspace placeholder short-circuits that gate: the cache hit
  // proves the ambient header already matches, so the fetch (and the instant
  // paint below) can run in parallel with the resolver instead of behind it.
  const resolution = useProjectResolution(platformProjectId);

  const {
    data: project,
    isLoading,
    error,
  } = useQuery({
    // The seam's interstitial owns the not-found UX, not the global toast.
    ...projectQuery(platformProjectId, { suppressErrorNotification: true }),
    enabled: !!platformProjectId && (resolution.status === 'ready' || !!placeholder),
    placeholderData: placeholder,
  });
  const shareModal = useProjectMembersModal(project);
  const createFolderModal = useModalState();

  // Hosted by the shell rather than per section so the drawer survives moving
  // between sections, and every section can reach it through the outlet
  // context. Must sit above the early returns below.
  const chainDrawer = useApprovalChainsDrawer();
  const [createFolderParentId, setCreateFolderParentId] = useState<string | undefined>();

  // A detail route replaces the project's chrome with its own header rather
  // than sitting under it: the asset and the brief each carry their own
  // breadcrumb back up the tree, so the project header and the section tabs
  // would be a second, redundant way to say where you are.
  const matches = useMatches();
  const hidesProjectChrome = matches.some(
    (match) => (match.handle as RouteChrome | undefined)?.hidesProjectChrome === true,
  );

  // Header state, lifted here from Overview: one instance above the outlet
  // serves every section, which is what stops each one building its own. This
  // shell renders on every section, so it sits outside the folders route guard
  // and has to consult both flags itself.
  const approvalChainsEnabled = useApprovalChainsEnabled();
  const canEditDetails = useHasProjectPermission([PROJECT_PERMISSIONS.ProjectEditDetails]);
  const editModal = useModalState();
  const renameProject = useUpdatePublishedProject({ workspaceId: activeWorkspaceId });

  // The rename field clears its draft the moment it commits, so the text snaps
  // back to the old name whether or not the write landed. Nothing else surfaces
  // the failure either: this endpoint sends X-Suppress-Error-Notification, and
  // the app deliberately wires no MutationCache onError. Without this catch a
  // 409 on a duplicate title, or any network error, is completely silent.
  // The name is written into the cache before the request goes out, so the header
  // shows the new one the instant the user commits. Without it the field reverts
  // to the old name and sits there until the refetch lands, which reads as the
  // rename having been ignored.
  //
  // The rollback is what makes that optimism safe, and it earns its keep twice:
  // nothing else would report a failure. This endpoint sends
  // X-Suppress-Error-Notification and the app deliberately wires no
  // MutationCache onError, so a 409 on a duplicate title would otherwise leave
  // the wrong name on screen with no explanation.
  const handleRename = useCallback(
    (title: string) => {
      if (!platformProjectId) return;

      const key = projectQuery(platformProjectId).queryKey;
      const previous = queryClient.getQueryData<ProjectDetailType>(key);
      queryClient.setQueryData<ProjectDetailType>(key, (current) =>
        current ? { ...current, title } : current,
      );

      renameProject.mutate(
        { projectId: platformProjectId, payload: { title } },
        {
          onError: (error) => {
            queryClient.setQueryData(key, previous);
            const { detail } = parseProblemDetail(error, t('projectDetailHeader.renameFailed'));
            notificationService.showError(detail);
          },
        },
      );
    },
    [platformProjectId, queryClient, renameProject, t],
  );

  const openCreateFolderModal = useCallback(
    (parentId?: string) => {
      setCreateFolderParentId(parentId);
      createFolderModal.open();
    },
    [createFolderModal],
  );

  const openShareModal = useCallback(() => {
    shareModal.open();
  }, [shareModal]);

  // With an active-workspace placeholder the shell paints immediately while
  // the resolver settles in the background (it can only land on `ready` —
  // the cache hit already proved same-workspace ownership).
  if ((resolution.status === 'resolving' && !placeholder) || isLoading) {
    return (
      <div
        className="flex h-full flex-1 items-center justify-center"
        data-testid="project-detail-loading"
      >
        <Spinner size="xl" className="text-muted-foreground" />
      </div>
    );
  }

  if (isTerminalResolution(resolution)) {
    return <DeepLinkInterstitial state={resolution} entity="project" />;
  }

  if (error || !project) {
    return (
      <EmptyState
        title={t('projectDetail.projectNotFound')}
        description={t('projectDetail.weCouldnTLoadThe')}
      >
        <button type="button" onClick={() => void navigate(-1)} className="underline">
          {t('projectDetail.back')}
        </button>
      </EmptyState>
    );
  }

  const outletContext: ProjectDetailOutletContext = {
    project,
    openCreateFolderModal,
    openShareModal,
    canShare: shareModal.canShare,
    chainDrawer,
  };

  return (
    <SelectedProjectProvider project={project}>
      {/* Header, tabs and content are siblings on the page frame with the
          canvas showing between them, rather than a nav column beside a card. */}
      <div className="flex h-full w-full min-h-0 flex-col gap-app-inset overflow-hidden p-app-inset">
        {!hidesProjectChrome && (
          <>
            <ProjectDetailHeader
              project={project}
              onShare={openShareModal}
              shareDisabled={!project.platformProjectId}
              onManageApprovalChains={approvalChainsEnabled ? chainDrawer.openList : undefined}
              onEditProjectDetail={editModal.open}
              onRename={canEditDetails ? handleRename : undefined}
            />
            <ProjectDetailTabs />
          </>
        )}
        <div className="flex min-h-0 flex-1 flex-col overflow-auto">
          <SectionTransition>
            <Outlet context={outletContext} />
          </SectionTransition>
        </div>
      </div>

      <ApprovalChainsDrawer drawer={chainDrawer} projectId={project.platformProjectId} />

      {editModal.isMounted && (
        <EditProjectModal
          key={editModal.instanceKey}
          open={editModal.isOpen}
          project={project}
          onClose={editModal.close}
        />
      )}

      {createFolderModal.isMounted && (
        <CreateFolderModal
          key={createFolderModal.instanceKey}
          open={createFolderModal.isOpen}
          platformProjectId={project.platformProjectId}
          parentId={createFolderParentId}
          onClose={createFolderModal.close}
        />
      )}

      {shareModal.isMounted &&
        project.platformProjectId &&
        shareModal.workspaceId &&
        shareModal.ownerId && (
          <ProjectMembersModal
            key={shareModal.instanceKey}
            open={shareModal.isOpen}
            workspaceId={shareModal.workspaceId}
            projectId={project.id}
            platformProjectId={project.platformProjectId}
            ownerId={shareModal.ownerId}
            onClose={shareModal.close}
          />
        )}
    </SelectedProjectProvider>
  );
}
